import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Mail, Chrome } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export function Login() {
  const { user, loading, signInWithGoogle, signInWithMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [showMagic, setShowMagic] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleGoogle = async () => {
    await signInWithGoogle();
  };

  const handleMagic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    const { error } = await signInWithMagicLink(email);
    setSending(false);
    if (error) { toast.error(error); return; }
    setMagicSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-indigo-100 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-brand-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white text-2xl font-bold">F</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Family Assets</h1>
          <p className="text-gray-500 text-sm mt-1">Track vehicles, appliances & reminders</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <button
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-700 font-medium hover:border-brand-400 hover:bg-brand-50 active:bg-brand-100 transition-all shadow-sm"
          >
            <Chrome className="w-5 h-5 text-brand-600" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {!showMagic && !magicSent && (
            <button
              onClick={() => setShowMagic(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-brand-600 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Sign in with email link instead
            </button>
          )}

          {showMagic && !magicSent && (
            <form onSubmit={handleMagic} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? 'Sending…' : 'Send magic link'}
              </button>
            </form>
          )}

          {magicSent && (
            <div className="text-center py-2">
              <p className="text-sm text-gray-700">✉️ Check your inbox for a sign-in link.</p>
              <p className="text-xs text-gray-400 mt-1">You can close this tab.</p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          By signing in you agree to keep your family's data safe.
        </p>
      </div>
    </div>
  );
}
